import { useEffect, useRef, useState } from 'react'
import { debounce } from 'throttle-debounce'
import { PaginatedResponse } from 'utils/types'

import api from '../api/api'
import useEnhancedState from './useEnhancedState'

function usePagination<T>(url: string, itemsToDownload: number, params: object = {}) {
  const [data, setData] = useState<T[]>([])
  const lock = useRef(false)
  const offset = useRef(0)
  const [state, setState] = useEnhancedState({
    total: 0,
    loading: false,
    error: false,
    initialLoaded: false
  })

  const fetchCurrentPageData = async () => {
    lock.current = true
    if (state.initialLoaded) setState({ loading: true })

    const response = await api.get<PaginatedResponse<T>>(url, {
      params: {
        ...params,
        $skip: offset.current,
        $limit: itemsToDownload
      }
    })

    return response.data
  }

  const onFetchSuccess = ({ results, status }: PaginatedResponse<T>) => {
    setData((d) => [...d, ...results])
    setState(() => ({
      loading: false,
      initialLoading: false,
      initialLoaded: true,
      total: status.total,
      error: false
    }))

    offset.current += results.length
    lock.current = false
  }

  const onFetchError = () => {
    lock.current = false
    setState({
      error: true,
      loading: false
    })
  }

  const fetchData = debounce(300, async () => {
    const canLoadMore = offset.current > 0 ? offset.current < state.total : true
    if (!canLoadMore || state.loading || lock.current) return

    fetchCurrentPageData().then(onFetchSuccess).catch(onFetchError)
  })

  useEffect(() => {
    setData([])
    setState({ total: 0, loading: false })
    lock.current = false
    offset.current = 0
  }, [])

  return { fetchData, data, error: state.error, loading: state.loading, initialLoading: state.initialLoaded === false }
}

export default usePagination
